import { ImportOrderSetLineInput } from '@warebee/shared/data-access-api-dto';
import { RawBuilder, SelectQueryBuilder, sql } from 'kysely';
import {
  AthenaTableType,
  makeMappingError,
  MappingSchema,
  PostMappingValidationOptions,
} from '../mapping';

export const ORDER_SET_MAPPING_SCHEMA: MappingSchema<ImportOrderSetLineInput> =
  {
    title: 'Order set',
    description: 'Contains order and order lines to be analyzed',
    partitionBy: [
      {
        engine: AthenaTableType.ICEBERG,
        key: 'orderDate',
        transform: 'day',
      },
    ],
    fields: [
      {
        name: 'consignee',
        type: 'string',
        title: 'Client (Consignee)',
        description:
          'Client (consignee) — Name of your company (can be generated) or your client (For 3PLs)',
      },
      {
        name: 'orderId',
        type: 'string',
        title: 'Order Id',
        description: 'Order Number',
      },
      {
        name: 'orderLine',
        type: 'integer',
        title: 'Order Line',
        description: 'Order Line',
      },
      {
        name: 'sku',
        type: 'string',
        title: 'Item',
        description: 'Item code (SKU)',
      },
      {
        name: 'quantity',
        type: 'integer',
        title: 'Quantity',
        description: 'Quantity ordered in the lowest unit of measure (UOM)',
      },
      {
        name: 'orderDate',
        type: 'localDateTime',
        measureType: 'local-date-time',
        measureValue: 'yyyy-MM-dd HH:mm:ss',
        optional: true,
        title: 'Order (Pick By) Date',
        description:
          'Order (Pick By) Date, Date time format example (multiple formats supported). Like: yyyy-mm-dd hh:mm:ss (ISO 8601)',
      },
      {
        name: 'customer',
        type: 'string',
        optional: true,
        title: 'Customer',
        description: 'Order Customer ID (Order level)',
      },
      {
        name: 'deliveryRoute',
        type: 'string',
        optional: true,
        title: 'Route',
        description: 'Transport/Delivery Route, Pick by Delivery, Pick by Trip',
      },
      {
        name: 'dock',
        type: 'string',
        optional: true,
        title: 'Dock',
        description: 'Used for Pick by Dock/Door',
      },

      {
        name: 'stagingArea',
        type: 'string',
        optional: true,
        title: 'Staging Area',
        description: 'Warehouse Staging Area',
      },
      {
        name: 'waveId',
        type: 'string',
        optional: true,
        title: 'Wave ID',
        description: 'Picking Wave ID, for wave/cluster picking method',
      },
      {
        name: 'orderType',
        type: 'string',
        optional: true,
        title: 'Order Type',
        description: 'Order Type',
      },
      {
        name: 'shipmentId',
        type: 'string',
        optional: true,
        title: 'Shipment ID',
        description: 'Shipment/Delivery identifier for grouping orders',
      },
      {
        name: 'loadByDate',
        type: 'localDateTime',
        measureType: 'local-date-time',
        optional: true,
        title: 'Load by Date',
        description: 'Date and time when order needs to be loaded to transport',
      },
      {
        name: 'pickingPriority',
        type: 'string',
        optional: true,
        title: 'Picking Priority ID',
        description: 'Picking priority ID for “shop friendly” picking.',
      },
      {
        name: 'pickingContainer',
        type: 'string',
        optional: true,
        title: 'Picking Container',
        description:
          'Picking container, when picking in batches of cages, totes or bin containers.',
      },
    ],
    dateField: 'orderDate',
    createValidationQueries: (
      src: RawBuilder<any>,
      lineNumberRef: RawBuilder<any>,
      options?: PostMappingValidationOptions,
      targetQb?: SelectQueryBuilder<any, any, ImportOrderSetLineInput>,
    ) =>
      [
        sql`
            select
              ${makeMappingError({
                code: sql.lit('BusinessLogicValidationError'),
                type: sql.lit('Order Date validation error'),
                property: sql.lit('orderDate'),
                message: sql`'Order ' || order_id || ' have different order date: ' || array_join(array_agg(distinct order_date), ', ')`,
                lines: sql`array_agg(${lineNumberRef})`,
              })}
            from ${src}
            where order_date is not null
            group by consignee, order_id
            having count(distinct order_date) > 1
          `,
        sql`
            select
              ${makeMappingError({
                code: sql.lit('BusinessLogicValidationError'),
                type: sql.lit('Load By Date validation error'),
                property: sql.lit('loadByDate'),
                message: sql`'Order ' || order_id || ' have different load by date: ' || array_join(array_agg(distinct load_by_date), ', ')`,
                lines: sql`array_agg(${lineNumberRef})`,
              })}
            from ${src}
            where load_by_date is not null
            group by consignee, order_id
            having count(distinct load_by_date) > 1
          `,
        sql`
            select
              ${makeMappingError({
                code: sql.lit('BusinessLogicValidationError'),
                type: sql.lit('Shipment id validation error'),
                property: sql.lit('shipmentId'),
                message: sql`'Order ' || order_id || ' have different shipment ids: ' || array_join(array_agg(distinct shipment_id), ', ')`,
                lines: sql`array_agg(${lineNumberRef})`,
              })}
            from ${src}
            where shipment_id is not null
            group by consignee, order_id
            having count(distinct shipment_id) > 1
          `,
      ] as any,
    primaryKey: ['consignee', 'orderId', 'orderLine'],
    appendable: true,
  };
